import { Card, Text } from "@tremor/react";
import type { TranslateFn } from "#/lib/i18n.js";

type OutbreakSource = {
  sourceId: string;
  slug: string;
  name: string;
  description: string | null;
  homepageUrl: string;
  aboutUrl: string;
  sourceUrl: string;
  verifiedAt: string;
};

type Props = {
  sources: OutbreakSource[];
  locale: string;
  t: TranslateFn;
};

function SourceCard({
  source,
  locale,
  t,
}: {
  source: OutbreakSource;
  locale: string;
  t: TranslateFn;
}) {
  const cardStyle: React.CSSProperties = {
    background: "var(--color-bg-surface)",
    border: "1px solid var(--color-border-default)",
    borderRadius: "0.5rem",
    padding: "1rem",
    minBlockSize: "5.5rem",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  };

  return (
    <Card style={cardStyle}>
      <div>
        <Text
          style={{
            color: "var(--color-fg-default)",
            fontWeight: 600,
            fontSize: "0.8125rem",
            marginBlockEnd: "0.25rem",
          }}
        >
          {source.name}
        </Text>
        {source.description && (
          <Text
            style={{
              color: "var(--color-fg-muted)",
              fontSize: "0.75rem",
              lineHeight: 1.4,
            }}
          >
            {source.description.slice(0, 160)}
            {source.description.length > 160 ? "…" : ""}
          </Text>
        )}
      </div>
      <a
        href={source.homepageUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${t("pathogen.viewSite")} — ${source.name}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minBlockSize: "2.75rem",
          minInlineSize: "2.75rem",
          marginBlockStart: "0.5rem",
          color: "var(--color-accent-info)",
          fontSize: "0.75rem",
          fontWeight: 500,
          textDecoration: "none",
          gap: "0.25rem",
        }}
      >
        {t("pathogen.viewSite")}
        <span
          aria-hidden="true"
          style={{
            transform: locale === "ar" ? "scaleX(-1)" : undefined,
            display: "inline-block",
          }}
        >
          →
        </span>
      </a>
    </Card>
  );
}

export function OfficialSourcesBlock({ sources, locale, t }: Props) {
  if (!sources || sources.length === 0) return null;

  return (
    <section aria-label={t("pathogen.officialSources")} style={{ marginBlockStart: "2rem" }}>
      <h2
        style={{
          fontSize: "1rem",
          fontWeight: 600,
          color: "var(--color-fg-default)",
          marginBlockEnd: "1rem",
        }}
      >
        {t("pathogen.officialSources")}
      </h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "1rem",
        }}
      >
        {sources.map((source) => (
          <SourceCard key={source.slug} source={source} locale={locale} t={t} />
        ))}
      </div>
    </section>
  );
}
